import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiAcceptInvite, apiRequestPasswordReset } from "@/lib/authApi";
import {
  useAdminLoginMutation,
  useIamLogoutMutation,
} from "@/graphql/generated";
import { useSessionKeepAlive } from "@/hooks/useSessionKeepAlive";
import {
  AUTH_STORAGE_KEY,
  clearStoredSession,
  persistStoredSession,
  safeParseSession,
  type StoredSession,
} from "@/lib/authStorage";
import {
  getJwtExpiryMs,
  refreshSessionIfNeeded,
  SESSION_REFRESHED_EVENT,
  type SessionRefreshedEventDetail,
} from "@/lib/sessionRefresh";

export type AuthUser = {
  id: string;
  name: string;
  email: string;
  role: "ADMIN" | "OPS" | "SUPPORT" | "UNKNOWN" | (string & {});
  avatar?: string;
  status?: string;
  lastLoginAt?: string;
  createdAt?: string;
};

type LoginResult =
  | { ok: true }
  | {
      ok: false;
      reason:
        | "INVALID_CREDENTIALS"
        | "ACCOUNT_BLOCKED"
        | "ACCOUNT_DISABLED"
        | "NOT_ACTIVATED"
        | "UNKNOWN";
      message?: string;
    };

type AcceptInviteResult =
  | { ok: true }
  | {
      ok: false;
      reason: "INVALID" | "EXPIRED" | "ALREADY_USED" | "UNKNOWN";
      message?: string;
    };

type AuthContextType = {
  isInitializing: boolean;
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<
    | { ok: true }
    | {
        ok: false;
        reason:
          | "EMAIL_NOT_FOUND"
          | "ACCOUNT_BLOCKED"
          | "NOT_ACTIVATED"
          | "UNKNOWN";
      }
  >;
  acceptInvite: (params: {
    token: string;
    password: string;
  }) => Promise<AcceptInviteResult>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function getGraphqlErrorCode(error: unknown): string | undefined {
  const err = error as {
    response?: {
      errors?: Array<{
        extensions?: {
          code?: unknown;
        };
      }>;
    };
    code?: unknown;
    extensions?: {
      code?: unknown;
    };
  };
  const code =
    err.response?.errors?.[0]?.extensions?.code ??
    err.code ??
    err.extensions?.code;

  return typeof code === "string" ? code : undefined;
}

function getGraphqlErrorMessage(error: unknown): string | undefined {
  const err = error as {
    response?: { errors?: Array<{ message?: unknown }> };
  };
  const message = err.response?.errors?.[0]?.message;
  return typeof message === "string" ? message : undefined;
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

// auth server returns FORBIDDEN for all of these; the message text disambiguates.
const SERVER_LOGIN_MESSAGE = {
  ACCOUNT_DISABLED: "Account is disabled. Contact support.",
  ACCOUNT_NOT_ACTIVE:
    "Account is not active. Please verify your email or contact support.",
} as const;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isInitializing, setIsInitializing] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);

  const adminLoginMutation = useAdminLoginMutation();
  const iamLogoutMutation = useIamLogoutMutation();

  useSessionKeepAlive({ enabled: !!token && !!user, refreshOnMount: true });

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      const existing = safeParseSession(localStorage.getItem(AUTH_STORAGE_KEY));
      if (!existing) {
        if (!cancelled) setIsInitializing(false);
        return;
      }

      const expiryMs = getJwtExpiryMs(existing.token);
      const shouldRefreshBeforeRestore =
        !expiryMs || expiryMs <= Date.now() + 30_000;

      if (shouldRefreshBeforeRestore) {
        const refreshResult = await refreshSessionIfNeeded({ force: true });
        if (cancelled) return;

        if (refreshResult.ok && "accessToken" in refreshResult) {
          setToken(refreshResult.accessToken);
          setUser(existing.user);
        } else {
          clearStoredSession();
          setToken(null);
          setUser(null);
        }
        setIsInitializing(false);
        return;
      }

      if (!cancelled) {
        setToken(existing.token);
        setUser(existing.user);
        setIsInitializing(false);
      }
    }

    void restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleSessionRefreshed = (event: Event) => {
      const detail = (event as CustomEvent<SessionRefreshedEventDetail>).detail;
      if (typeof detail?.accessToken === "string") {
        setToken(detail.accessToken);
      }
    };

    window.addEventListener(SESSION_REFRESHED_EVENT, handleSessionRefreshed);
    return () => {
      window.removeEventListener(
        SESSION_REFRESHED_EVENT,
        handleSessionRefreshed,
      );
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      try {
        const data = await adminLoginMutation.mutateAsync({ email, password });
        const payload = data?.adminLogin;
        const accessToken = payload?.accessToken;
        const refreshToken = payload?.refreshToken;
        const u = payload?.user;
        if (!accessToken || !refreshToken || !u)
          throw new Error("Login failed");

        const uExt = u as typeof u & {
          roles?: string[] | null;
        };
        const fromRoles =
          Array.isArray(uExt.roles) && uExt.roles.length > 0
            ? String(uExt.roles[0]).trim()
            : "";
        const fromRole = typeof uExt.role === "string" ? uExt.role.trim() : "";
        const normalizedRole = fromRoles || fromRole;
        if (!normalizedRole) {
          throw new Error("Login failed: missing user role");
        }

        const session: StoredSession = {
          token: accessToken,
          refreshToken,
          user: {
            id: u.id,
            email: u.email,
            name: u.name || u.email,
            role: normalizedRole as AuthUser["role"],
            status: u.status,
            lastLoginAt: u.lastLoginAt ? String(u.lastLoginAt) : undefined,
            createdAt: u.createdAt ? String(u.createdAt) : undefined,
          },
        };

        setToken(session.token);
        setUser(session.user);
        persistStoredSession(session);
        return { ok: true };
      } catch (e) {
        const code = getGraphqlErrorCode(e);
        const message = getGraphqlErrorMessage(e);

        // Bad email/password → typically UNAUTHORIZED (legacy: INVALID_CREDENTIALS / UNAUTHENTICATED).
        if (
          code === "UNAUTHORIZED" ||
          code === "INVALID_CREDENTIALS" ||
          code === "UNAUTHENTICATED"
        )
          return { ok: false, reason: "INVALID_CREDENTIALS" };

        if (code === "ACCOUNT_BLOCKED")
          return { ok: false, reason: "ACCOUNT_BLOCKED" };

        // Disabled / inactive / wrong-portal → all FORBIDDEN; message disambiguates.
        if (code === "FORBIDDEN") {
          if (message?.startsWith(SERVER_LOGIN_MESSAGE.ACCOUNT_DISABLED))
            return { ok: false, reason: "ACCOUNT_DISABLED" };
          if (message?.startsWith(SERVER_LOGIN_MESSAGE.ACCOUNT_NOT_ACTIVE))
            return { ok: false, reason: "NOT_ACTIVATED" };
          return { ok: false, reason: "UNKNOWN", message };
        }

        return {
          ok: false,
          reason: "UNKNOWN",
          message: getErrorMessage(e, "Login failed"),
        };
      }
    },
    [adminLoginMutation],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await iamLogoutMutation.mutateAsync({});
    } catch {
      // server-side revocation is best-effort; always clear local session
    } finally {
      setToken(null);
      setUser(null);
      clearStoredSession();
    }
  }, [iamLogoutMutation]);

  const requestPasswordReset = useCallback(async (email: string) => {
    try {
      return await apiRequestPasswordReset(email);
    } catch (e) {
      const err = e as Error & { status?: number; code?: string };
      if (err.code === "ACCOUNT_BLOCKED")
        return { ok: false as const, reason: "ACCOUNT_BLOCKED" as const };
      if (err.code === "NOT_ACTIVATED")
        return { ok: false as const, reason: "NOT_ACTIVATED" as const };
      if (err.status === 404)
        return { ok: false as const, reason: "EMAIL_NOT_FOUND" as const };
      return { ok: false as const, reason: "UNKNOWN" as const };
    }
  }, []);

  const acceptInvite = useCallback(
    async (params: { token: string; password: string }) => {
      try {
        const res = await apiAcceptInvite(params);
        return res.ok
          ? ({ ok: true } as const)
          : ({ ok: false, reason: "UNKNOWN" } as const);
      } catch (e) {
        const err = e as Error & { status?: number; code?: string };
        if (err.code === "INVALID")
          return { ok: false as const, reason: "INVALID" as const };
        if (err.code === "EXPIRED")
          return { ok: false as const, reason: "EXPIRED" as const };
        if (err.code === "ALREADY_USED")
          return { ok: false as const, reason: "ALREADY_USED" as const };
        return {
          ok: false as const,
          reason: "UNKNOWN" as const,
          message: err.message,
        };
      }
    },
    [],
  );

  const value = useMemo<AuthContextType>(
    () => ({
      isInitializing,
      isAuthenticated: !!token && !!user,
      user,
      token,
      login,
      logout,
      requestPasswordReset,
      acceptInvite,
    }),
    [
      isInitializing,
      token,
      user,
      login,
      logout,
      requestPasswordReset,
      acceptInvite,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
